/**
 * Build mailto: and Outlook on the web compose URLs from compose_email / SendEmailInput-shaped payloads.
 * URLs are length-limited; body (and if needed subject) are truncated when necessary.
 */

const MAILTO_MAX_LENGTH = 2000;
/** OWA compose URLs can be longer than mailto: but browsers and gateways still cap total length. */
const OUTLOOK_WEB_COMPOSE_MAX_LENGTH = 6000;

const OUTLOOK_WEB_COMPOSE_BASE = "https://outlook.office.com/mail/deeplink/compose";

export function normalizeRecipientList(val: unknown): string[] {
  if (Array.isArray(val)) {
    return val.map((x) => String(x).trim()).filter(Boolean);
  }
  if (typeof val === "string") {
    const s = val.trim();
    if (!s) return [];
    try {
      const parsed = JSON.parse(s);
      if (Array.isArray(parsed)) {
        return parsed.map((x) => String(x).trim()).filter(Boolean);
      }
    } catch {
      /* use as raw */
    }
    return s
      .split(/[,;]/)
      .map((x) => x.trim().replace(/^[\s["']+|[\s\]"']+$/g, ""))
      .filter(Boolean);
  }
  return [];
}

function htmlToPlainTextForCompose(html: string): string {
  return html
    // Compose deeplinks are plain text. Preserve an HTML anchor's destination instead of
    // reducing `<a ...>Click for Password</a>` to unusable text with no URL.
    .replace(
      /<a\b[^>]*href=["']([^"']+)["'][^>]*>([\s\S]*?)<\/a>/gi,
      (_match, href, label) =>
        `${String(label).replace(/<[^>]+>/g, "").trim()} (${String(href).replace(/&amp;/gi, "&")})`
    )
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<\/p>/gi, "\n")
    .replace(/<\/div>/gi, "\n")
    .replace(/<[^>]+>/g, "")
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/gi, "&")
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

export type PreparedSendEmailCompose = {
  to: string[];
  subject: string;
  body: string;
  cc: string;
  bcc: string;
};

/**
 * Normalized To/Subject/Body/CC/BCC for mailto and OWA compose links.
 */
export function prepareM365SendEmailComposeParts(
  emailInput: Record<string, unknown>
): PreparedSendEmailCompose | null {
  const to = normalizeRecipientList(emailInput.to_recipients);
  if (to.length === 0) return null;

  const subject = String(emailInput.subject ?? "");
  const bodyType = String(emailInput.body_type ?? "Text").toUpperCase();
  let body = String(emailInput.body ?? "");
  if (bodyType === "HTML") {
    body = htmlToPlainTextForCompose(body);
  }

  const cc = normalizeRecipientList(emailInput.cc_recipients).join(",");
  const bcc = normalizeRecipientList(emailInput.bcc_recipients).join(",");

  return { to, subject, body, cc, bcc };
}

/**
 * Query encoding for compose URLs. Avoid URLSearchParams.toString() here: it uses "+" for spaces
 * (application/x-www-form-urlencoded). Outlook on the web often shows those "+" literally in the
 * body/subject instead of decoding them as spaces. encodeURIComponent uses %20 / %0A which OWA handles.
 */
function appendEncodedQueryPair(pairs: string[], key: string, value: string): void {
  pairs.push(`${encodeURIComponent(key)}=${encodeURIComponent(value)}`);
}

function buildMailtoHref(parts: PreparedSendEmailCompose): string {
  const pairs: string[] = [];
  if (parts.subject) appendEncodedQueryPair(pairs, "subject", parts.subject);
  if (parts.body) appendEncodedQueryPair(pairs, "body", parts.body);
  if (parts.cc) appendEncodedQueryPair(pairs, "cc", parts.cc);
  if (parts.bcc) appendEncodedQueryPair(pairs, "bcc", parts.bcc);
  const qs = pairs.join("&");
  const addr = parts.to.join(",");
  return qs ? `mailto:${addr}?${qs}` : `mailto:${addr}`;
}

function buildOutlookWebComposeUrl(parts: PreparedSendEmailCompose): string {
  const pairs: string[] = [];
  if (parts.to.length) appendEncodedQueryPair(pairs, "to", parts.to.join(","));
  if (parts.subject) appendEncodedQueryPair(pairs, "subject", parts.subject);
  if (parts.body) appendEncodedQueryPair(pairs, "body", parts.body);
  if (parts.cc) appendEncodedQueryPair(pairs, "cc", parts.cc);
  if (parts.bcc) appendEncodedQueryPair(pairs, "bcc", parts.bcc);
  const qs = pairs.join("&");
  return qs ? `${OUTLOOK_WEB_COMPOSE_BASE}?${qs}` : OUTLOOK_WEB_COMPOSE_BASE;
}

function fitComposeToMaxLength(
  initial: PreparedSendEmailCompose,
  maxLen: number,
  buildUrl: (p: PreparedSendEmailCompose) => string
): { parts: PreparedSendEmailCompose; truncated: boolean } {
  const suffix = "\n\n[... truncated by Theo: link length limit ...]";
  let parts = { ...initial };
  let truncated = false;
  let url = buildUrl(parts);

  while (url.length > maxLen) {
    truncated = true;
    if (parts.body.length > 120) {
      parts = {
        ...parts,
        body:
          parts.body.slice(0, Math.max(80, Math.floor(parts.body.length * 0.75))) + suffix,
      };
    } else if (parts.subject.length > 40) {
      parts = { ...parts, subject: parts.subject.slice(0, 30) + "…" };
    } else {
      break;
    }
    url = buildUrl(parts);
    if (parts.body.length <= 120 && parts.subject.length <= 40) break;
  }

  return { parts, truncated };
}

/**
 * Merge base args_json with HITL edits for this tool index.
 * Supports both the flat compose_email shape (top-level to_recipients/subject/body/...)
 * and the legacy m365_send_email shape (nested under email_input).
 */
export function mergeM365SendEmailInput(
  argsObj: Record<string, unknown>,
  editedToolArgs: Record<string, unknown> | undefined
): Record<string, unknown> | null {
  // Flat shape: compose_email args have top-level to_recipients/subject/body fields.
  if (argsObj?.to_recipients != null || argsObj?.subject != null) {
    const edits = editedToolArgs && typeof editedToolArgs === "object" && !Array.isArray(editedToolArgs)
      ? editedToolArgs as Record<string, unknown>
      : {};
    return { ...argsObj, ...edits };
  }
  // Legacy nested shape: m365_send_email wraps fields under email_input.
  const base = argsObj?.email_input;
  if (base == null || typeof base !== "object" || Array.isArray(base)) return null;
  const b = base as Record<string, unknown>;
  const e = editedToolArgs?.email_input;
  if (e != null && typeof e === "object" && !Array.isArray(e)) {
    return { ...b, ...(e as Record<string, unknown>) };
  }
  return { ...b };
}

export function buildMailtoFromSendEmailInput(
  emailInput: Record<string, unknown>
): { href: string; truncated: boolean } {
  const prepared = prepareM365SendEmailComposeParts(emailInput);
  if (!prepared) return { href: "", truncated: false };

  const { parts, truncated } = fitComposeToMaxLength(
    prepared,
    MAILTO_MAX_LENGTH,
    buildMailtoHref
  );
  return { href: buildMailtoHref(parts), truncated };
}

export function buildOutlookWebComposeFromSendEmailInput(
  emailInput: Record<string, unknown>
): { url: string; truncated: boolean; unsupportedRecipients: boolean; ccPromotedToTo: boolean } {
  const prepared = prepareM365SendEmailComposeParts(emailInput);
  if (!prepared) return { url: "", truncated: false, unsupportedRecipients: false, ccPromotedToTo: false };
  // Outlook Web's /mail/deeplink/compose contract supports to/subject/body only.
  // It silently ignores bcc, so do not offer a misleading link in that case.
  if (prepared.bcc) {
    return { url: "", truncated: false, unsupportedRecipients: true, ccPromotedToTo: false };
  }
  // For the explicit Outlook Web fallback, promote configured CC addresses into To so they
  // are not silently dropped. The standards-based mailto path retains true CC semantics.
  const ccRecipients = normalizeRecipientList(prepared.cc);
  const to = [...prepared.to];
  const seen = new Set(to.map((recipient) => recipient.toLowerCase()));
  for (const recipient of ccRecipients) {
    if (!seen.has(recipient.toLowerCase())) {
      to.push(recipient);
      seen.add(recipient.toLowerCase());
    }
  }
  const owaPrepared = { ...prepared, to, cc: "" };

  const { parts, truncated } = fitComposeToMaxLength(
    owaPrepared,
    OUTLOOK_WEB_COMPOSE_MAX_LENGTH,
    buildOutlookWebComposeUrl
  );
  return {
    url: buildOutlookWebComposeUrl(parts),
    truncated,
    unsupportedRecipients: false,
    ccPromotedToTo: ccRecipients.length > 0,
  };
}
